/**
 * Requisitions — asking, approving, authorising and paying, kept apart.
 *
 * The three columns are deliberately not one number: asked for commits
 * nothing, committed is money spoken for but not yet gone, and paid has left
 * the bank. Each currency stands on its own.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BadgeCheck, Banknote, FileText, Loader2, Paperclip, Send, ShieldCheck, XCircle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import CurrencySelect from '@/components/CurrencySelect';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-amber-100 text-amber-800',
  returned: 'bg-orange-100 text-orange-800',
  approved: 'bg-indigo-100 text-indigo-800',
  authorised: 'bg-sky-100 text-sky-800',
  paid: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  cancelled: 'bg-slate-100 text-slate-500',
};

const STATUS_WORDS = {
  submitted: 'awaiting a decision',
  approved: 'committed, not yet paid',
  authorised: 'cleared for payment',
};

const money = (n, ccy) => `${ccy || ''} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

const CATEGORIES = ['equipment', 'maintenance', 'utilities', 'travel', 'materials',
  'hospitality', 'salaries', 'rent', 'general'];

const DOC_TYPES = [
  { value: 'quotation', label: "Supplier's quotation" },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'other', label: 'Other paper' },
];

/** Upload one document against a requisition. Private storage, short link. */
const uploadDocument = async (token, requisitionId, file, docType) => {
  const body = new FormData();
  body.append('file', file);
  body.append('doc_type', docType || 'quotation');
  const r = await fetch(
    `${API_URL}/api/finance/requisitions/${requisitionId}/attachments`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body },
  );
  const out = await r.json();
  if (!r.ok) throw new Error(out.detail || 'That document would not attach');
  return out;
};

/** Open a private document.
 *
 * These are served only to whoever can prove who they are, on every request,
 * so the address cannot simply go in an <a href> — the browser would send no
 * token. We fetch it with ours and hand the browser the bytes.
 */
const openDocument = async (token, attachment) => {
  const r = await fetch(`${API_URL}${attachment.url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    throw new Error(r.status === 403
      ? 'This document belongs to another assembly'
      : 'That document could not be opened');
  }
  const blob = await r.blob();
  const href = URL.createObjectURL(blob);
  const opened = window.open(href, '_blank', 'noopener');
  if (!opened) {
    const link = document.createElement('a');
    link.href = href;
    link.download = attachment.filename || 'document';
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(href), 60000);
};

const AttachmentList = ({ attachments, onRemove, removable, testPrefix }) => {
  const { token } = useAuthStore();
  if (!attachments?.length) return null;
  return (
    <ul className="space-y-1" data-testid={`${testPrefix}-list`}>
      {attachments.map((a, i) => (
        <li key={a.id || i} className="flex items-center justify-between gap-2 text-sm">
          <button type="button"
                  onClick={() => openDocument(token, a).catch((e) => toast.error(e.message))}
                  className="flex items-center gap-1.5 text-indigo-700 hover:underline"
                  data-testid={`${testPrefix}-${i}`}>
            <Paperclip className="h-3.5 w-3.5" />
            {a.filename}
            <span className="text-[11px] capitalize text-slate-500">
              · {a.doc_type}{a.after_decision ? ' · added after the decision' : ''}
            </span>
          </button>
          {removable ? (
            <Button size="sm" variant="ghost" onClick={() => onRemove(a)}
                    data-testid={`${testPrefix}-remove-${i}`}>
              <XCircle className="h-3.5 w-3.5 text-rose-500" />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

export const RequisitionsPanel = ({ unitId }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [decide, setDecide] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [attachTo, setAttachTo] = useState(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const { data, isLoading } = useQuery({
    queryKey: ['requisitions', unitId || 'all'],
    queryFn: async () => {
      const qs = unitId ? `?unit_id=${unitId}` : '';
      const r = await fetch(`${API_URL}/api/finance/requisitions${qs}`, { headers });
      if (!r.ok) throw new Error('Could not load requisitions');
      return r.json();
    },
    enabled: !!token,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['requisitions'] });

  const act = useMutation({
    mutationFn: async ({ id, path, body }) => {
      const r = await fetch(`${API_URL}/api/finance/requisitions/${id}/${path}`, {
        method: 'POST', headers, body: JSON.stringify(body || {}),
      });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'That could not be done');
      return out;
    },
    onSuccess: (out, vars) => {
      refresh();
      setDecide(null);
      setPayFor(null);
      if (vars.path === 'pay') {
        toast.success(out.duplicate ? 'Already paid — nothing was paid twice'
          : 'Paid, and posted to the ledger once');
      } else {
        toast.success('Done');
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = data?.requisitions || [];
  const byCurrency = Object.entries(data?.summary?.by_currency || {});

  return (
    <div className="space-y-4" data-testid="requisitions-panel">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-500" />Requisitions
            </CardTitle>
            <CardDescription>
              Asking, approving, authorising and paying — four acts, never one
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setNewOpen(true)} disabled={!unitId}
                  data-testid="requisition-new-btn">
            New requisition
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {!unitId ? (
            <p className="text-sm text-slate-500" data-testid="requisitions-pick-unit">
              Choose a unit to raise a requisition for it.
            </p>
          ) : null}

          {byCurrency.length ? (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="requisition-summary">
              {byCurrency.map(([ccy, t]) => (
                <div key={ccy} className="rounded-lg border p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{ccy}</p>
                  <dl className="mt-1 space-y-0.5 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Asked for</dt>
                      <dd>{money(t.requested, ccy)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Committed</dt>
                      <dd className="font-medium">{money(t.committed, ccy)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Paid</dt>
                      <dd className="font-semibold">{money(t.paid, ccy)}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : null}

          {isLoading ? (
            <p className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />Looking…
            </p>
          ) : !rows.length ? (
            <p className="py-6 text-center text-sm text-slate-500"
               data-testid="requisitions-empty">
              Nothing has been asked for yet.
            </p>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>What for</TableHead>
                    <TableHead>Asked by</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Where it stands</TableHead>
                    <TableHead className="text-right">Next</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.id} data-testid={`requisition-row-${i}`}>
                      <TableCell>
                        <p className="font-medium text-slate-800">{r.title}</p>
                        <p className="text-xs text-slate-500">
                          {r.category}{r.supplier ? ` · ${r.supplier}` : ''}
                          {r.budget_line ? ` · ${r.budget_line}` : ''}
                        </p>
                        {r.attachment_count ? (
                          <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500"
                                data-testid={`requisition-papers-${i}`}>
                            <Paperclip className="h-3 w-3" />
                            {r.attachment_count} document{r.attachment_count === 1 ? '' : 's'}
                            {r.has_quotation ? ' · quotation' : ''}
                          </span>
                        ) : (
                          <span className="mt-0.5 block text-[11px] text-amber-700"
                                data-testid={`requisition-no-quote-${i}`}>
                            no quotation attached
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {r.requester_name || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {money(r.approved_amount ?? r.amount, r.currency)}
                        {r.approved_amount != null && r.approved_amount !== r.amount ? (
                          <span className="block text-[11px] text-amber-700">
                            part of {money(r.amount, r.currency)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_STYLES[r.status] || ''}>{r.status}</Badge>
                        <span className="ml-1.5 text-[11px] text-slate-500">
                          {STATUS_WORDS[r.status] || ''}
                        </span>
                        {r.budget_override ? (
                          <span className="block text-[11px] text-rose-700"
                                data-testid={`requisition-override-${i}`}>
                            over budget · {r.budget_override.reason}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        {!['rejected', 'cancelled'].includes(r.status) ? (
                          <Button size="sm" variant="ghost" onClick={() => setAttachTo(r)}
                                  data-testid={`requisition-attach-${i}`}>
                            <Paperclip className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                        {r.status === 'draft' ? (
                          <Button size="sm" variant="outline"
                                  onClick={() => act.mutate({ id: r.id, path: 'submit' })}
                                  data-testid={`requisition-submit-${i}`}>
                            <Send className="mr-1 h-3.5 w-3.5" />Submit
                          </Button>
                        ) : null}
                        {r.status === 'submitted' ? (
                          <Button size="sm" variant="outline" onClick={() => setDecide(r)}
                                  data-testid={`requisition-decide-${i}`}>
                            <BadgeCheck className="mr-1 h-3.5 w-3.5" />Decide
                          </Button>
                        ) : null}
                        {r.status === 'approved' ? (
                          <Button size="sm" variant="outline"
                                  onClick={() => act.mutate({ id: r.id, path: 'authorise' })}
                                  data-testid={`requisition-authorise-${i}`}>
                            <ShieldCheck className="mr-1 h-3.5 w-3.5" />Authorise
                          </Button>
                        ) : null}
                        {r.status === 'authorised' ? (
                          <Button size="sm" onClick={() => setPayFor(r)}
                                  data-testid={`requisition-pay-${i}`}>
                            <Banknote className="mr-1 h-3.5 w-3.5" />Pay
                          </Button>
                        ) : null}
                        {['draft', 'submitted', 'returned'].includes(r.status) ? (
                          <Button size="sm" variant="ghost"
                                  onClick={() => act.mutate({ id: r.id, path: 'cancel' })}
                                  data-testid={`requisition-cancel-${i}`}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {newOpen ? (
        <NewRequisition unitId={unitId} onClose={() => setNewOpen(false)}
                        onDone={() => { setNewOpen(false); refresh(); }} />
      ) : null}
      {decide ? (
        <DecideDialog requisition={decide} onClose={() => setDecide(null)}
                      onDecide={(body) => act.mutate({ id: decide.id, path: 'decide', body })}
                      pending={act.isPending} />
      ) : null}
      {payFor ? (
        <PayDialog requisition={payFor} onClose={() => setPayFor(null)}
                   onPay={(body) => act.mutate({ id: payFor.id, path: 'pay', body })}
                   pending={act.isPending} />
      ) : null}
      {attachTo ? (
        <AttachDialog requisition={attachTo} onClose={() => setAttachTo(null)}
                      onChanged={refresh} />
      ) : null}
    </div>
  );
};

const NewRequisition = ({ unitId, onClose, onDone }) => {
  const { token } = useAuthStore();
  const [form, setForm] = useState({
    title: '', amount: '', currency: 'USD', category: 'equipment', supplier: '',
    budget_line: '', needed_by: '', purpose: '', notes: '',
  });

  // A budget line only measures anything if it is one the unit actually keeps,
  // so the requester picks from them rather than typing a near-miss.
  const { data: budget } = useQuery({
    queryKey: ['budgets', unitId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/budgets?unit_id=${unitId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('unavailable');
      return r.json();
    },
    enabled: !!token && !!unitId,
    retry: false,
  });
  const budgetLines = budget?.lines || [];
  const chosen = budgetLines.find((l) => l.line === form.budget_line);
  const [quote, setQuote] = useState(null);

  const save = useMutation({
    mutationFn: async () => {
      // With a quotation in hand the request is created as a draft, the paper
      // is attached, and only then is it submitted — so the approver never
      // sees it before its evidence.
      const r = await fetch(`${API_URL}/api/finance/requisitions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form, unit_id: unitId, amount: Number(form.amount), submit: !quote,
        }),
      });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'Could not save that');
      if (quote) {
        const id = out.requisition.id;
        await uploadDocument(token, id, quote, 'quotation');
        const sent = await fetch(`${API_URL}/api/finance/requisitions/${id}/submit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const submitted = await sent.json();
        if (!sent.ok) throw new Error(submitted.detail || 'Attached, but not submitted');
      }
      return out;
    },
    onSuccess: () => { toast.success('Submitted for approval'); onDone(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="requisition-form">
        <DialogHeader><DialogTitle className="text-base">New requisition</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">What is it for</Label>
            <Input value={form.title} data-testid="requisition-title"
                   placeholder="Sound equipment for the main hall"
                   onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" value={form.amount} data-testid="requisition-amount"
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Currency</Label>
              <CurrencySelect value={form.currency} showHomeHint={false}
                              testId="requisition-currency"
                              onChange={(c) => setForm({ ...form, currency: c })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category}
                      onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="requisition-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Needed by</Label>
              <Input type="date" value={form.needed_by} data-testid="requisition-needed-by"
                     onChange={(e) => setForm({ ...form, needed_by: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Supplier (if known)</Label>
              <Input value={form.supplier} data-testid="requisition-supplier"
                     onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Budget line</Label>
              {budgetLines.length ? (
                <Select value={form.budget_line || 'none'}
                        onValueChange={(v) => setForm({
                          ...form, budget_line: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="requisition-budget-line">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No budget line</SelectItem>
                    {budgetLines.map((l) => (
                      <SelectItem key={l.id} value={l.line}>
                        {l.line} · {money(l.available, l.currency)} left
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.budget_line} data-testid="requisition-budget-line"
                       placeholder="This unit keeps no budget yet"
                       onChange={(e) => setForm({ ...form, budget_line: e.target.value })} />
              )}
              {chosen && chosen.available !== null
                && Number(form.amount) > Number(chosen.available) ? (
                  <p className="mt-1 text-xs text-rose-600"
                     data-testid="requisition-over-budget-hint">
                    Over this line by {money(Number(form.amount) - Number(chosen.available),
                      chosen.currency)} — an officer may still approve it, with a reason.
                  </p>
                ) : null}
            </div>
          </div>
          <div>
            <Label className="text-xs">Why it is needed</Label>
            <Textarea rows={2} value={form.purpose} data-testid="requisition-purpose"
                      onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Supplier's quotation</Label>
            <Input type="file" data-testid="requisition-quote"
                   onChange={(e) => setQuote(e.target.files?.[0] || null)} />
            <p className="mt-1 text-xs text-slate-500">
              {quote
                ? `${quote.name} will be attached before this reaches the approver.`
                : 'Optional, but an approver asked for a figure with no quotation is guessing.'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.title || !(Number(form.amount) > 0) || save.isPending}
                  onClick={() => save.mutate()} data-testid="requisition-save">
            {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Submit for approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AttachDialog = ({ requisition, onClose, onChanged }) => {
  const { token } = useAuthStore();
  const [docType, setDocType] = useState('quotation');
  const [attachments, setAttachments] = useState(requisition.attachments || []);
  const [busy, setBusy] = useState(false);
  const undecided = ['draft', 'submitted', 'returned'].includes(requisition.status);

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const out = await uploadDocument(token, requisition.id, file, docType);
      setAttachments(out.requisition.attachments || []);
      onChanged();
      toast.success('Attached');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    setBusy(true);
    try {
      const r = await fetch(
        `${API_URL}/api/finance/requisitions/${requisition.id}/attachments/${a.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
      );
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'That could not be removed');
      setAttachments(out.requisition.attachments || []);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="requisition-attach-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Documents · {requisition.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <AttachmentList attachments={attachments} onRemove={remove}
                          removable={undecided} testPrefix="attachment" />
          {!attachments.length ? (
            <p className="text-sm text-slate-500" data-testid="attachment-empty">
              Nothing attached yet. A quotation tells the approver what the
              supplier actually charges.
            </p>
          ) : null}
          <div>
            <Label className="text-xs">What is this paper</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="attachment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((d) => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Choose the file</Label>
            <Input type="file" disabled={busy} data-testid="attachment-file"
                   onChange={(e) => send(e.target.files?.[0])} />
            <p className="mt-1 text-xs text-slate-500">
              Kept private — only finance officers over this unit can open it.
              {undecided ? '' : ' This request has been decided, so anything added now is stamped as such.'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="attachment-done">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DecideDialog = ({ requisition, onClose, onDecide, pending }) => {
  const [amount, setAmount] = useState(String(requisition.amount));
  const [notes, setNotes] = useState('');
  const [override, setOverride] = useState('');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="requisition-decide-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{requisition.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Asked for {money(requisition.amount, requisition.currency)} by{' '}
            {requisition.requester_name || 'a colleague'}.
            {requisition.purpose ? ` ${requisition.purpose}` : ''}
          </p>
          <div className="rounded-lg border bg-slate-50 p-2">
            <p className="mb-1 text-xs font-medium text-slate-600">What was attached</p>
            <AttachmentList attachments={requisition.attachments}
                            testPrefix="decide-attachment" />
            {!requisition.has_quotation ? (
              <p className="text-xs text-amber-700" data-testid="decide-no-quotation">
                No supplier quotation on this request — you are approving a figure
                nobody has evidenced.
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-xs">Approve how much</Label>
            <Input type="number" value={amount} data-testid="decide-amount"
                   onChange={(e) => setAmount(e.target.value)} />
            <p className="mt-1 text-xs text-slate-500">
              You may approve less than was asked for, never more.
            </p>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} data-testid="decide-notes"
                      onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Reason, if this goes over budget</Label>
            <Input value={override} data-testid="decide-override"
                   placeholder="Only needed if the budget line is short"
                   onChange={(e) => setOverride(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" disabled={pending}
                  onClick={() => onDecide({ action: 'return', notes })}
                  data-testid="decide-return">Return for more</Button>
          <div className="space-x-2">
            <Button variant="outline" disabled={pending}
                    onClick={() => onDecide({ action: 'reject', notes })}
                    data-testid="decide-reject">Reject</Button>
            <Button disabled={pending}
                    onClick={() => onDecide({
                      action: 'approve', amount: Number(amount), notes,
                      budget_override_reason: override,
                    })}
                    data-testid="decide-approve">
              {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Approve
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PayDialog = ({ requisition, onClose, onPay, pending }) => {
  const authorised = requisition.approved_amount ?? requisition.amount;
  const [amount, setAmount] = useState(String(authorised));
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="requisition-pay-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Record the payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Authorised: {money(authorised, requisition.currency)}. Recording this
            writes the expense and posts the ledger once — it does not move money
            at the bank.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount paid</Label>
              <Input type="number" value={amount} data-testid="pay-amount"
                     onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['bank_transfer', 'cash', 'card', 'mobile_money', 'cheque'].map((m) => (
                    <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Reference</Label>
            <Input value={reference} data-testid="pay-reference"
                   placeholder="Bank reference or cheque number"
                   onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={pending}
                  onClick={() => onPay({ amount: Number(amount), payment_method: method, reference })}
                  data-testid="pay-confirm">
            {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequisitionsPanel;
